// Mock data sourced/adapted from https://novocreste.codenurture.com/
// NOTE: All statistics, certifications, countries, categories and process steps
// below are taken from the existing Novocreste website. Nothing is invented.

export const company = {
  name: "Novocreste International",
  legalName: "Novocreste International Pvt Ltd",
  tagline: "Your trusted partner connecting a worldwide manufacturer network to global healthcare",
  intro:
    "Connecting pharmaceutical companies worldwide with trusted manufacturing partners across every dosage form, category and regulated market.",
  email: "info@novocreste.com",
  phone: "+91 8169686328",
  phoneHref: "+918169686328",
};

export const stats = [
  { value: "100+", label: "Manufacturing Partners" },
  { value: "1300+", label: "Products in Portfolio" },
  { value: "15+", label: "Therapeutic Categories Covered" },
  { value: "50+", label: "Countries Distributors Network" },
];

export const whyChoose = [
  {
    icon: "Network",
    title: "Extensive Network",
    desc: "Access to a wide range of qualified pharmaceutical manufacturers worldwide.",
  },
  {
    icon: "ShieldCheck",
    title: "Regulatory Expertise",
    desc: "Partners with accreditations across major global regulatory bodies.",
  },
  {
    icon: "UserRound",
    title: "One Point of Contact",
    desc: "Single dedicated team managing your entire sourcing journey.",
  },
  {
    icon: "Timer",
    title: "Faster Execution",
    desc: "Streamlined process and pre-qualified partners reduce lead times.",
  },
  {
    icon: "BadgeIndianRupee",
    title: "Competitive Pricing",
    desc: "Best price advantage without compromising on quality.",
  },
  {
    icon: "Radar",
    title: "Real-time Tracking",
    desc: "Auto updates and tracking at every stage of your order.",
  },
];

export const services = [
  {
    icon: "Factory",
    title: "Contract Manufacturing",
    desc: "Trusted contract manufacturers for all dosage forms and categories.",
  },
  {
    icon: "ClipboardList",
    title: "Dossier & Regulatory Support",
    desc: "Complete dossier preparation and regulatory compliance support.",
  },
  {
    icon: "FileCheck2",
    title: "Product Registration Support",
    desc: "End-to-end registration support for global markets.",
  },
  {
    icon: "Gavel",
    title: "Tender Supply",
    desc: "Supply to government tenders and institutional procurement.",
  },
  {
    icon: "FileText",
    title: "Export Documentation",
    desc: "Complete documentation support for smooth global exports.",
  },
  {
    icon: "Truck",
    title: "Supply Chain Management",
    desc: "Reliable logistics and supply chain management from start to finish.",
  },
];

export const productCategories = [
  { name: "Prescription Medicines", short: "Prescription", group: "Product Categories" },
  { name: "OTC Products", short: "OTC", group: "Product Categories" },
  { name: "Nutraceuticals", short: "Nutraceuticals", group: "Product Categories" },
  { name: "Dietary Supplements", short: "Dietary", group: "Product Categories" },
  { name: "Herbal Products", short: "Herbal", group: "Product Categories" },
  { name: "Cosmetics", short: "Cosmetics", group: "Product Categories" },
  { name: "Medical Devices", short: "Medical", group: "Product Categories" },
  { name: "Veterinary Products", short: "Veterinary", group: "Product Categories" },
];

export const dosageForms = [
  "Tablets",
  "Capsules",
  "Injectables",
  "Syrups",
  "Inhalation Products",
  "Eye Drops",
  "Ear Drops",
  "Creams",
  "Ointments",
  "Gels",
  "Lotions",
  "Powders",
  "Sachets",
  "Effervescent Tablets",
  "Suppositories",
];

const slugify = (s) =>
  s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// A unified export-style directory combining dosage forms + product categories.
// Descriptions are generic capability statements (no invented approvals/claims).
export const products = [
  ...dosageForms.map((name) => ({
    slug: slugify(name),
    name,
    group: "Dosage Forms",
    category: "Dosage Forms",
    blurb: `Sourced across our qualified manufacturing network in ${name.toLowerCase()} format, with documentation and compliance handled end-to-end.`,
  })),
  ...productCategories.map((c) => ({
    slug: slugify(c.name),
    name: c.name,
    group: "Product Categories",
    category: "Product Categories",
    blurb: `A broad portfolio within ${c.name.toLowerCase()}, sourced from pre-qualified partners for regulated global markets.`,
  })),
];

export const featuredProducts = [
  { slug: "tablets", name: "Tablets", category: "Dosage Forms" },
  { slug: "injectables", name: "Injectables", category: "Dosage Forms" },
  { slug: "capsules", name: "Capsules", category: "Dosage Forms" },
  { slug: "nutraceuticals", name: "Nutraceuticals", category: "Product Categories" },
  { slug: "prescription-medicines", name: "Prescription Medicines", category: "Product Categories" },
  { slug: "syrups", name: "Syrups", category: "Dosage Forms" },
];

export const regions = [
  "Middle East",
  "Africa",
  "North Africa",
  "Latin America",
  "Asia",
  "CIS Countries",
  "Europe",
  "Balkans",
  "US",
];

export const certifications = [
  "US FDA",
  "EU GMP",
  "UK MHRA",
  "PIC/S",
  "WHO GMP",
  "TGA Australia",
  "Health Canada",
  "SFDA Saudi",
  "ANVISA Brazil",
  "DIGEMID Peru",
  "NAFDAC Nigeria",
  "MOH Ghana",
];

export const processSteps = [
  { n: "01", title: "Receive RFQ", desc: "Share your requirement and we log your enquiry." },
  { n: "02", title: "Requirement Analysis", desc: "We study specs, markets and regulatory needs." },
  { n: "03", title: "Manufacturer Matching", desc: "We match you with qualified, accredited partners." },
  { n: "04", title: "Quotation & Negotiation", desc: "Transparent pricing and commercial clarity." },
  { n: "05", title: "Documentation & Compliance", desc: "Dossiers, registration and export paperwork." },
  { n: "06", title: "Production & Delivery", desc: "Reliable production, logistics and delivery." },
];

export const testimonials = [
  {
    quote:
      "Novocreste has been a reliable partner in sourcing quality pharmaceutical products. Their regulatory knowledge, quick response and transparency make them stand out.",
    name: "Ahmed Al Mansoori",
    role: "Procurement Manager, UAE",
  },
];

export const images = {
  heroLab: "https://images.unsplash.com/photo-1607398027609-fbd1a06fb5d4",
  pills: "https://images.unsplash.com/photo-1631549916768-4119b2e5f926",
  labTech: "https://images.unsplash.com/photo-1551884170-09fb70a3a2ed",
  blister: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae",
  containersSunset: "https://images.unsplash.com/photo-1759272840538-ae4b07214c71",
  cargoAerial: "https://images.unsplash.com/photo-1724597500306-a4cbb7d1324e",
  portCrane: "https://images.unsplash.com/photo-1700777685830-f501e67260e6",
  containersStacked: "https://images.unsplash.com/photo-1703227373720-cff89520dd31",
};

// Home hero slider (editable in Admin → Site Content → Hero & Images)
export const heroSlides = [
  { image: images.heroLab, alt: "Pharmaceutical cleanroom manufacturing" },
  { image: images.pills, alt: "Medicine tablets and capsules" },
  { image: images.blister, alt: "Blister packs of medicines" },
  { image: images.containersSunset, alt: "Export containers at sunset" },
  { image: images.cargoAerial, alt: "Global cargo shipping" },
];
export const heroInterval = 5;

export const navLinks = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Products", to: "/products" },
  { label: "Services", to: "/services" },
  { label: "Quality & Compliance", to: "/quality" },
  { label: "Global Presence", to: "/global-presence" },
  { label: "Contact", to: "/contact" },
  { label: "Blog", to: "/blog" },
];
